/* =============================================================================
 * M.M.LaFleur — pause/play control for looping background videos.
 *
 * Every muted, looping video in the editorial layer (media rows, PDP gallery,
 * hero) plays on its own and never stops. WCAG 2.2.2 requires a way to pause
 * anything that moves for more than five seconds, and a visitor who has asked
 * the OS for reduced motion should not get it at all until they opt in.
 *
 * MARKUP CONTRACT
 * ---------------
 *   <div data-mm-video-scope>
 *     <video data-mm-video ...>
 *     <button data-mm-video-toggle data-mm-paused="false"
 *             data-label-pause="Pause video" data-label-play="Play video"
 *             aria-label="Pause video">…</button>
 *   </div>
 *
 * The button's state is DERIVED from the video's own play/pause events rather
 * than tracked separately. Anything that stops the video — this control, the
 * reduced-motion guard below, iOS Low Power Mode refusing autoplay — flips the
 * label to "Play", so the button can never claim a video is playing when it
 * is not.
 *
 * THE GUARD
 * ---------
 * Pausing is not enough on its own: the `autoplay` attribute and
 * mm-editorial.js initVideos() both call play() again (the latter on every
 * section load and Barba swap). A capture-phase `play` listener on document
 * catches every one of those — media events do not bubble, but they do
 * capture — and re-pauses a video that is:
 *   - data-mm-user-paused="true" (the visitor pressed Pause), or
 *   - under prefers-reduced-motion and not data-mm-user-played="true".
 * Being attribute-driven, the guard also covers the hero, whose single
 * slideshow control (mm-hero-autoplay.js) sets the same attributes.
 *
 * Safe to include from several sections on one page: the listeners are
 * delegated on document and bound once behind a window flag, the same pattern
 * mm-editorial.js uses.
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmVideoControlsBound) return;
  window.__mmVideoControlsBound = true;

  function reducedMotion() {
    return !!(window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  function videoFor(button) {
    var scope = button.closest("[data-mm-video-scope]");
    return scope ? scope.querySelector("video") : null;
  }

  function sync(video) {
    var scope = video.closest("[data-mm-video-scope]");
    if (!scope) return;
    var paused = video.paused;
    [].slice.call(scope.querySelectorAll("[data-mm-video-toggle]")).forEach(function (button) {
      button.setAttribute("data-mm-paused", String(paused));
      var label = button.getAttribute(paused ? "data-label-play" : "data-label-pause");
      if (label) button.setAttribute("aria-label", label);
    });
  }

  function shouldHold(video) {
    if (video.getAttribute("data-mm-user-paused") === "true") return true;
    return reducedMotion() && video.getAttribute("data-mm-user-played") !== "true";
  }

  function init() {
    [].slice.call(document.querySelectorAll("video[data-mm-video]")).forEach(function (video) {
      if (shouldHold(video) && !video.paused) video.pause();
      sync(video);
    });
  }

  document.addEventListener("play", function (event) {
    var video = event.target;
    if (!video || video.tagName !== "VIDEO" || !video.hasAttribute("data-mm-video")) return;
    if (shouldHold(video)) {
      video.pause();
      return;
    }
    sync(video);
  }, true);

  document.addEventListener("pause", function (event) {
    var video = event.target;
    if (!video || video.tagName !== "VIDEO" || !video.hasAttribute("data-mm-video")) return;
    sync(video);
  }, true);

  /* Capture + stopPropagation, per the theme's rule for Barba and for the
     PDP gallery's own click handlers (zoom, Shop this look): the toggle sits
     over the media and a click on it must not be read as a click on the tile. */
  document.addEventListener("click", function (event) {
    if (!event.target || !event.target.closest) return;
    var button = event.target.closest("[data-mm-video-toggle]");
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    var video = videoFor(button);
    if (!video) return;

    if (video.paused) {
      video.removeAttribute("data-mm-user-paused");
      video.setAttribute("data-mm-user-played", "true");
      video.muted = true;
      var p = video.play();
      if (p && p.catch) p.catch(function () { sync(video); });
    } else {
      video.setAttribute("data-mm-user-paused", "true");
      video.pause();
    }
  }, true);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("platter-cache-busted", init);
}());
